import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { getActiveConnection, getHoldResumeConnection } from './contactUtils';
import { ElapsedTimer } from './ElapsedTimer';
import { DtmfKeypad, DTMF_DIGITS, type DtmfEntry } from './DtmfKeypad';
import { playDtmfTone } from '@/utils/dtmfTone';
import type { ConnectContact, ConnectAgent } from '@/types';
import {
  Paper,
  Typography,
  Box,
  Button,
  Popover,
  Tooltip,
} from '@gapartners/ui';
import {
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  CallEnd as CallEndIcon,
  Dialpad as DialpadIcon,
} from '@mui/icons-material';
import { pauseCallRecording, resumeCallRecording } from '@/store/api/call';
import { setCustomerOnHold, setRecordingState } from '@/store/slices/callSlice';
import { selectIsWarmInProgress } from '@/store/slices/warmTransferSlice';

interface ActiveCallBannerProps {
  contact: ConnectContact | null;
  agent: ConnectAgent | null;
  muted: boolean;
  /** When true, show chat-specific UI (no mute/hold/speaker/recording). */
  isChat?: boolean;
  onEndCall: () => void;
}

export function ActiveCallBanner({
  contact,
  agent,
  muted,
  isChat = false,
  onEndCall,
}: ActiveCallBannerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    recordingState,
    isDirectConnect,
    callerPhoneNumber,
    customerOnHold,
    customerOnHoldSince,
    authenticated,
  } = useAppSelector(state => state.call);
  const { isConsultCall, cachedConsultContext } = useAppSelector(
    state => state.warmTransfer
  );
  const isWarmInProgress = useAppSelector(selectIsWarmInProgress);
  const { callerName, clientAlert } = useAppSelector(state => state.call);
  const sessionStartedAt = useAppSelector(state => state.call.startedAt);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isRecordingToggling, setIsRecordingToggling] = useState(false);
  const [keypadAnchorEl, setKeypadAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [dtmfEntries, setDtmfEntries] = useState<DtmfEntry[]>([]);
  /** Bumped on Clear and on contact change so stale in-flight sendDigits callbacks are ignored. */
  const dtmfSessionRef = useRef(0);

  const connection = getActiveConnection(contact);
  const holdResumeConnection = getHoldResumeConnection(contact);
  let connectionId: string | null = null;
  try {
    connectionId = connection?.getConnectionId?.() ?? null;
  } catch {
    // connection may already be destroyed remotely (StateError)
  }
  let contactId: string | null = null;
  try {
    contactId = contact?.getContactId?.() ?? null;
  } catch {
    // contact may already be destroyed remotely (StateError)
  }
  const phoneNumber = isDirectConnect
    ? t('components.contactCenter.directConnectPhoneHidden')
    : (callerPhoneNumber ?? '-');

  let holdResumeOnHold = false;
  try {
    holdResumeOnHold = Boolean(holdResumeConnection?.isOnHold?.());
  } catch {
    // connection may already be destroyed remotely (StateError)
  }
  const isCustomerOnHold = isPaused || customerOnHold || holdResumeOnHold;
  const dtmfConnection = holdResumeConnection ?? connection;
  const canSendDigits =
    !isChat &&
    !isCustomerOnHold &&
    dtmfConnection != null &&
    typeof dtmfConnection.sendDigits === 'function';

  /**
   * Last on-hold state observed from the connection. Only a false→true
   * transition is synced into redux: a connection that merely stays held
   * (e.g. stale state right after a resume) must not re-stamp the timer.
   */
  const prevOnHoldRef = useRef<boolean | undefined>(undefined);
  /**
   * True when the current redux hold was synced by the poll itself (not by an
   * explicit Hold/Resume action in this UI). For such holds the connection is
   * authoritative in both directions — e.g. a warm transfer completing clears
   * the customer leg's hold, and the receiving agent's UI must follow it.
   */
  const holdSyncedFromPollRef = useRef(false);

  useEffect(() => {
    if (!holdResumeConnection) return;
    const syncHoldFromConnect = () => {
      let onHold = false;
      try {
        onHold = Boolean(holdResumeConnection.isOnHold?.());
      } catch {
        // connection may be stale
      }
      setIsPaused(onHold);
      // Holds started outside this UI (e.g. another CCP tab, or the customer
      // leg held during a warm consult) reach redux only through this poll.
      const prevOnHold = prevOnHoldRef.current;
      if (onHold !== prevOnHold) {
        console.log(
          `[ActiveCallBanner:hold] connection hold state changed: ${String(prevOnHold)} -> ${String(onHold)} (redux customerOnHold: ${String(customerOnHold)}, pollSynced: ${String(holdSyncedFromPollRef.current)})`
        );
      }
      prevOnHoldRef.current = onHold;
      if (onHold && prevOnHold !== true && !customerOnHold) {
        holdSyncedFromPollRef.current = true;
        console.log(
          `[ActiveCallBanner:hold] hold transition from connection — syncing into redux (connectionId: ${connectionId ?? 'unknown'})`
        );
        dispatch(setCustomerOnHold(true));
      } else if (
        !onHold &&
        prevOnHold === true &&
        customerOnHold &&
        holdSyncedFromPollRef.current
      ) {
        holdSyncedFromPollRef.current = false;
        console.log(
          '[ActiveCallBanner:hold] poll-synced hold cleared on connection — clearing redux'
        );
        dispatch(setCustomerOnHold(false));
      }
    };
    syncHoldFromConnect();
    const intervalId = setInterval(syncHoldFromConnect, 1000);
    return () => clearInterval(intervalId);
  }, [holdResumeConnection, connectionId, customerOnHold, dispatch]);

  const hasActiveLeg = connection != null;

  // Reset the digit log whenever the active contact changes or the call ends.
  useEffect(() => {
    dtmfSessionRef.current += 1;
    setDtmfEntries([]);
    setKeypadOpen(false);
  }, [contactId, hasActiveLeg]);

  const handleMuteToggle = () => {
    if (!agent?.mute || !agent?.unmute) return;
    if (muted) agent.unmute();
    else agent.mute();
  };

  const handleHold = () => {
    if (!holdResumeConnection || isCustomerOnHold || isTransitioning) return;
    if (typeof holdResumeConnection.hold !== 'function') {
      console.error('[ActiveCallBanner] Hold not available on this connection');
      return;
    }
    setIsTransitioning(true);
    holdResumeConnection.hold({
      success: () => {
        holdSyncedFromPollRef.current = false;
        setIsPaused(true);
        dispatch(setCustomerOnHold(true));
        setIsTransitioning(false);
      },
      failure: (err?: Error) => {
        console.error('[ActiveCallBanner] Hold failed', err);
        setIsTransitioning(false);
      },
    });
  };

  const handleResume = () => {
    if (!holdResumeConnection || !isCustomerOnHold || isTransitioning) return;
    if (typeof holdResumeConnection.resume !== 'function') {
      console.error(
        '[ActiveCallBanner] Resume not available on this connection'
      );
      return;
    }
    setIsTransitioning(true);
    holdResumeConnection.resume({
      success: () => {
        holdSyncedFromPollRef.current = false;
        setIsPaused(false);
        dispatch(setCustomerOnHold(false));
        setIsTransitioning(false);
      },
      failure: (err?: Error) => {
        console.error('[ActiveCallBanner] Resume failed', err);
        setIsTransitioning(false);
      },
    });
  };

  const handleToggleRecording = async () => {
    if (!contactId || !recordingState || isRecordingToggling) return;
    setIsRecordingToggling(true);
    try {
      const response =
        recordingState === 'enabled'
          ? await pauseCallRecording(contactId)
          : await resumeCallRecording(contactId);
      if (response?.success && response.recordingState) {
        dispatch(setRecordingState(response.recordingState));
      }
    } catch {
      // keep previous recordingState
    } finally {
      setIsRecordingToggling(false);
    }
  };

  const handleDtmfPress = (digit: string) => {
    if (!canSendDigits || !dtmfConnection) return;
    playDtmfTone(digit);
    const sessionAtPress = dtmfSessionRef.current;
    try {
      dtmfConnection.sendDigits(digit, {
        success: () => {
          if (sessionAtPress !== dtmfSessionRef.current) return;
          setDtmfEntries(prev => [...prev, { digit, status: 'sent' }]);
        },
        failure: () => {
          if (sessionAtPress !== dtmfSessionRef.current) return;
          setDtmfEntries(prev => [...prev, { digit, status: 'failed' }]);
        },
      });
    } catch {
      setDtmfEntries(prev => [...prev, { digit, status: 'failed' }]);
    }
  };

  // Always call the latest handleDtmfPress from the window keydown listener
  // without re-subscribing on every render.
  const dtmfPressRef = useRef(handleDtmfPress);
  useEffect(() => {
    dtmfPressRef.current = handleDtmfPress;
  });

  // Physical-keyboard input while the keypad popover is open (0-9, *, #).
  useEffect(() => {
    if (!keypadOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        // Don't hijack typing in form fields.
        return;
      }
      if (!DTMF_DIGITS.includes(event.key)) return;
      event.preventDefault();
      dtmfPressRef.current(event.key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keypadOpen]);

  if (!contact || !hasActiveLeg) {
    return null;
  }

  let queueName = '';
  try {
    queueName = contact.getQueue()?.name?.trim() ?? '';
  } catch {
    // contact may already be destroyed remotely (StateError)
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      {/* Header: title (Active Call / Active Chat) + ACTIVE or ON HOLD tag */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            color="text.primary"
            sx={{ fontSize: '1.125rem' }}
          >
            {isChat
              ? t('components.amazonConnect.activeChat')
              : t('components.amazonConnect.activeCall')}
          </Typography>
          {isPaused ? (
            <Box
              component="span"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                bgcolor: 'warning.main',
                color: '#FFF',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              {t('components.amazonConnect.onHold', 'On hold')}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                bgcolor: 'success.main',
                color: '#FFF',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              {t('components.contactCenter.active', 'ACTIVE')}
            </Box>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {`${t('components.amazonConnect.queue')}: ${queueName ?? 'Unknown'}`}
        </Typography>
        {isConsultCall && isCustomerOnHold ? (
          <Box
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: 'info.main',
              color: '#FFF',
              textTransform: 'uppercase',
            }}
          >
            {t(
              'components.contactCenter.warmTransferConsultBanner',
              'Warm consult'
            )}
          </Box>
        ) : isWarmInProgress ? (
          <Box
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: 'secondary.main',
              color: '#FFF',
              textTransform: 'uppercase',
            }}
          >
            {t(
              'components.contactCenter.warmTransferInProgress',
              'Warm transfer active'
            )}
          </Box>
        ) : isCustomerOnHold ? (
          <Box
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: 'warning.main',
              color: '#FFF',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
            }}
          >
            {t('components.amazonConnect.onHold', 'On hold')}
          </Box>
        ) : (
          <Box
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: 'success.main',
              color: '#FFF',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
            }}
          >
            {t('components.contactCenter.active', 'ACTIVE')}
          </Box>
        )}
      </Box>

      {/* Details: for voice show phone + duration; for chat show duration only */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
        {(() => {
          const isAuthenticated = authenticated === true;
          return (
            <Box
              component="span"
              sx={{
                alignSelf: 'flex-start',
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: isAuthenticated ? 'success.light' : 'warning.light',
                color: isAuthenticated ? 'success.dark' : 'warning.dark',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              {isAuthenticated
                ? t('components.amazonConnect.authentication.authenticated')
                : t('components.amazonConnect.authentication.notAuthenticated')}
            </Box>
          );
        })()}
        {isConsultCall &&
          isCustomerOnHold &&
          cachedConsultContext?.customerName && (
            <Typography variant="body2" color="text.secondary">
              {t('components.contactCenter.warmTransferHandoffCustomer', {
                name: cachedConsultContext.customerName,
                defaultValue: 'Customer on hold: {{name}}',
              })}
            </Typography>
          )}
        {isCustomerOnHold && !isChat && !isConsultCall && (
          <Typography variant="body2" color="warning.main" fontWeight={600}>
            {t(
              'components.contactCenter.customerOnHoldBannerHint',
              'Customer is on hold — press Resume to bring them back.'
            )}
          </Typography>
        )}
        {isCustomerOnHold && !isChat && customerOnHoldSince != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'warning.dark',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="body1"
              fontWeight={600}
              color="warning.main"
              sx={{ fontFamily: 'monospace' }}
            >
              {`${t('components.amazonConnect.onHoldFor', 'On hold for')} `}
              <ElapsedTimer startTime={customerOnHoldSince} />
            </Typography>
          </Box>
        )}
        {!!callerName?.trim() && !isConsultCall && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              color="text.primary"
              sx={{ wordBreak: 'break-word' }}
            >
              {callerName}
            </Typography>
            {!!clientAlert?.trim() && (
              <Typography
                variant="body2"
                fontWeight={500}
                color="warning.dark"
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'warning.light',
                  border: '1px solid',
                  borderColor: 'warning.main',
                  wordBreak: 'break-word',
                }}
              >
                {clientAlert}
              </Typography>
            )}
          </Box>
        )}

        {!isChat && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <PhoneIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              sx={{ fontFamily: 'monospace' }}
            >
              {phoneNumber}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="body1"
            fontWeight={500}
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {sessionStartedAt != null && (
              <ElapsedTimer startTime={sessionStartedAt} />
            )}
          </Typography>
        </Box>
      </Box>

      {/* Controls: for chat = End only; for voice = Mute, Speaker, Hold, Recording, End */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {!isChat && (
          <>
            <Button
              variant="outlined"
              onClick={handleMuteToggle}
              title={
                muted
                  ? t('components.amazonConnect.unmute')
                  : t('components.amazonConnect.mute')
              }
              sx={{
                minWidth: 44,
                minHeight: 44,
                p: 0,
                borderRadius: '50%',
                borderColor: 'divider',
                bgcolor: muted ? 'grey.100' : 'background.paper',
                '&:hover': {
                  borderColor: 'divider',
                  bgcolor: muted ? 'grey.200' : 'grey.100',
                },
              }}
            >
              {muted ? (
                <MicOffIcon sx={{ fontSize: 20 }} />
              ) : (
                <MicIcon sx={{ fontSize: 20 }} />
              )}
            </Button>

            {!isCustomerOnHold ? (
              <Button
                variant="outlined"
                onClick={handleHold}
                disabled={isTransitioning}
                title={t('components.amazonConnect.hold')}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  p: 0,
                  borderRadius: '50%',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  opacity: isTransitioning ? 0.5 : 1,
                  '&:hover': {
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <PauseIcon sx={{ fontSize: 20 }} />
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleResume}
                disabled={isTransitioning}
                title={t('components.amazonConnect.resume')}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  p: 0,
                  borderRadius: '50%',
                  borderColor: 'warning.main',
                  bgcolor: 'warning.light',
                  color: 'warning.dark',
                  opacity: isTransitioning ? 0.5 : 1,
                  '&:hover': {
                    borderColor: 'warning.main',
                    bgcolor: 'warning.light',
                  },
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 20 }} />
              </Button>
            )}

            {contactId && recordingState && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleRecording}
                disabled={isRecordingToggling}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  borderColor:
                    recordingState === 'enabled' ? 'divider' : 'warning.main',
                  bgcolor:
                    recordingState === 'enabled'
                      ? 'background.paper'
                      : 'warning.light',
                  color:
                    recordingState === 'enabled'
                      ? 'text.primary'
                      : 'warning.dark',
                  opacity: isRecordingToggling ? 0.5 : 1,
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor:
                      recordingState === 'enabled' ? 'divider' : 'warning.main',
                    bgcolor:
                      recordingState === 'enabled'
                        ? 'grey.100'
                        : 'warning.light',
                  },
                }}
              >
                {recordingState === 'enabled'
                  ? t('components.amazonConnect.pauseRecording')
                  : t('components.amazonConnect.resumeRecording')}
              </Button>
            )}

            <Tooltip
              title={
                !canSendDigits
                  ? t(
                      'components.amazonConnect.dtmfKeypadUnavailable',
                      'Keypad unavailable while the customer is on hold'
                    )
                  : t('components.amazonConnect.keypad')
              }
            >
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                  variant="outlined"
                  onClick={event => {
                    setKeypadAnchorEl(event.currentTarget);
                    setKeypadOpen(prev => !prev);
                  }}
                  disabled={!canSendDigits}
                  title={t('components.amazonConnect.keypad')}
                  sx={{
                    minWidth: 44,
                    minHeight: 44,
                    p: 0,
                    borderRadius: '50%',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    opacity: !canSendDigits ? 0.5 : 1,
                    '&:hover': {
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <DialpadIcon sx={{ fontSize: 20 }} />
                </Button>
              </Box>
            </Tooltip>
          </>
        )}

        <Button
          variant="contained"
          onClick={onEndCall}
          title={t('components.amazonConnect.endCall')}
          sx={{
            minWidth: 44,
            minHeight: 44,
            p: 0,
            borderRadius: '50%',
            bgcolor: 'error.main',
            color: 'error.contrastText',
            '&:hover': {
              bgcolor: 'error.dark',
            },
          }}
        >
          <CallEndIcon sx={{ fontSize: 20 }} />
        </Button>
      </Box>

      <Popover
        open={keypadOpen}
        anchorEl={keypadAnchorEl}
        onClose={() => setKeypadOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <DtmfKeypad
          entries={dtmfEntries}
          disabled={!canSendDigits}
          onPress={handleDtmfPress}
        />
      </Popover>
    </Paper>
  );
}
